import { Button, SpinnerLoader, ChevronLeft, ChevronRight, Toastify, Eye, EyeOff } from 'lift-ui';
import React, { useState, useEffect, useRef } from 'react';

import { useAlertFormData } from '../hooks/useAlertFormData';
import { useBrandManagement } from '../hooks/useBrandManagement';
import { Alert, Brand } from '../types';
import {
  dmgAlertApiService,
  SlackUser,
  SlackChannelWithDetails,
} from '../utils/dmgAlertApiService';
import { stripHtmlToPlainText } from '../utils/helpers';

import { AlertDetailsStep } from './AlertDetailsStep';
import { AlertSummaryStep } from './AlertSummaryStep';
import { BasicInformationStep } from './BasicInformationStep';
import { validateThreshold } from './BrandThresholdTable';
import { CommunicationStep } from './CommunicationStep';
import { MessageTemplateStep, MessageTemplateStepRef } from './MessageTemplateStep';

// Alert Types interface based on API response
interface AlertType {
  id: number;
  name: string;
  display_name: string;
  description: string;
  data_source: string;
  default_threshold_config?: Array<{
    value: number;
    metric: string;
    operator: '>' | '>=' | '<' | '<=' | '==' | '!=';
    max_value?: string | number;
    period_days?: number;
  }>;
  created_at: string;
  updated_at: string;
  alert_query_config?: {
    properties: string[];
  };
}

interface AlertFormProps {
  alert?: Alert | null;
  isDuplicating?: boolean;
  onClose: () => void;
  onSubmit: any;
  userEmail?: string;
  userIsTeam?: boolean;
  userId?: number;
  onStepChange?: (step: number, completedSteps: number[]) => void;
}

export const AlertForm = React.forwardRef<
  { handleStepClick: (_step: number) => void },
  AlertFormProps
>(({ alert, isDuplicating, onSubmit, userEmail, userIsTeam, userId, onStepChange }, ref) => {
  const [currentStep, setCurrentStep] = useState(1);
  const messageTemplateStepRef = React.useRef<MessageTemplateStepRef>(null);
  const [alertTypes, setAlertTypes] = useState<AlertType[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [slackUsers, setSlackUsers] = useState<SlackUser[]>([]);
  const [slackChannelsWithDetails, setSlackChannelsWithDetails] = useState<
    SlackChannelWithDetails[]
  >([]);
  const [teamUsers, setTeamUsers] = useState<Array<{ id: number; name: string; email: string }>>(
    []
  );
  const [selectedSlackRecipients, setSelectedSlackRecipients] = useState<string[]>([]);
  const [selectedCommunicationMethods, setSelectedCommunicationMethods] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [isLoadingCommunicationData, setIsLoadingCommunicationData] = useState(true);
  const [previewState, setPreviewState] = useState({ showPreview: false, isLoadingPreview: false });

  const { formData, setFormData, selectedBrands, setSelectedBrands, handleInputChange } =
    useAlertFormData({ alert, isDuplicating, userEmail, userIsTeam });

  // Keep a ref to always have the latest selectedBrands
  const selectedBrandsRef = useRef(selectedBrands);
  useEffect(() => {
    selectedBrandsRef.current = selectedBrands;
  }, [selectedBrands]);

  const {
    pixels,
    getEventsForThreshold,
    fetchEventsForPixels,
    addBrand,
    removeBrand,
    addThresholdRow,
    removeThresholdRow,
    updateThreshold,
    updateLogicalOperator,
    reorderThresholds,
    handlePixelSelection,
    refreshPixelsAndEvents,
    isLoadingEvents,
    clearAllThresholds,
  } = useBrandManagement({
    brands,
    selectedBrands,
    setSelectedBrands,
    setFormData,
    defaultOperator: alertTypes.find(at => at.display_name === formData.toolName)
      ?.default_threshold_config?.[0]?.operator,
    defaultValue: alertTypes.find(at => at.display_name === formData.toolName)
      ?.default_threshold_config?.[0]?.value,
    alert,
    alertTypeName: alertTypes.find(at => at.display_name === formData.toolName)?.name,
  });

  // Load initial data (alert types, slack users, etc.) - but NOT brands
  useEffect(() => {
    const loadData = async () => {
      setIsLoadingInitialData(true);
      setIsLoadingCommunicationData(true);
      try {
        const alertTypesResponse = await dmgAlertApiService.getAlertTypes();
        setAlertTypes(alertTypesResponse.alert_types);

        // Don't load brands here - they will be loaded when tool is selected
        setBrands([]);

        // Step 2 data loaded
        setIsLoadingInitialData(false);

        const slackUsersData = await dmgAlertApiService.getSlackUsers();
        setSlackUsers(slackUsersData);

        // Fetch team users (email dropdown) - always pass user_id and brand_id
        if (userId) {
          // Use first selected brand if available, otherwise first brand from alert
          let brandIdToUse: number | undefined;
          if (selectedBrands.length > 0) {
            brandIdToUse = selectedBrands[0].brandId;
          } else if (alert?.brands && alert.brands.length > 0) {
            brandIdToUse = alert.brands[0].brand_id;
          }

          if (brandIdToUse) {
            const teams = await dmgAlertApiService.getTeams(userId, brandIdToUse);
            setTeamUsers(teams);
          }
        }

        // Fetch detailed channel information if user email is available
        if (userEmail) {
          const slackChannelsWithDetailsData =
            await dmgAlertApiService.getSlackChannelsWithDetails(userEmail);
          setSlackChannelsWithDetails(slackChannelsWithDetailsData);
        }

        // Communication data loaded
        setIsLoadingCommunicationData(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setIsLoadingInitialData(false);
        setIsLoadingCommunicationData(false);
      }
    };

    loadData();

    // Set selected communication methods for MultiSelectTag
    if (alert) {
      setSelectedCommunicationMethods(
        alert.communicationMethods.filter(method => method.selected).map(method => method.id)
      );
      // Prefill slack recipients selection from formData
      if (formData.slackRecipients && formData.slackRecipients.length > 0) {
        setSelectedSlackRecipients(formData.slackRecipients);
      } else {
        if (alert?.slackRecipients && alert.slackRecipients.length > 0) {
          setSelectedSlackRecipients(alert.slackRecipients);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert, userEmail]);

  // Load brands when tool is selected
  useEffect(() => {
    // Wait for alertTypes to load and toolName to be set
    if (alertTypes.length === 0 || !formData.toolName) {
      setBrands([]);
      return;
    }

    const loadBrands = async () => {
      try {
        const selectedAlertType = alertTypes.find(at => at.display_name === formData.toolName);
        const isPacingTool = selectedAlertType?.name === 'pacing';
        const isTaxonomyTool = selectedAlertType?.name === 'taxonomy';

        let brandsData: Brand[] = [];
        if (isPacingTool) {
          const pacingBrands = await dmgAlertApiService.getPacingBrands();
          brandsData = pacingBrands.map(pb => ({
            id: String(pb.brand_id),
            name: pb.name,
            domain: '',
            brandId: pb.brand_id,
            isEmq: true,
          }));
        } else if (isTaxonomyTool) {
          const taxonomyBrands = await dmgAlertApiService.getTaxonomyBrands();
          brandsData = taxonomyBrands.map(tb => ({
            id: String(tb.id),
            name: tb.name,
            domain: '',
            brandId: tb.id,
            isEmq: false,
          }));
        } else {
          brandsData = (await dmgAlertApiService.getBrands()) as Brand[];
        }

        setBrands(brandsData);
      } catch (error) {
        console.error('Error loading brands:', error);
        setBrands([]);
      }
    };

    loadBrands();
  }, [formData.toolName, alertTypes]);

  // Sync selectedCommunicationMethods with formData.communicationMethods
  useEffect(() => {
    const selectedIds = formData.communicationMethods
      .filter(method => method.selected)
      .map(method => method.id);
    setSelectedCommunicationMethods(selectedIds);
  }, [formData.communicationMethods]);

  // Track the last brand ID we fetched teams for to avoid redundant calls
  const lastFetchedBrandIdRef = useRef<number | null>(null);

  // Get the first brand ID and memoize it to prevent unnecessary re-renders
  // Only trigger when the actual brand ID changes, not when selectedBrands array reference changes
  const firstBrandId = selectedBrands.length > 0 ? selectedBrands[0].brandId : null;

  // Refetch teams when the first brand ID changes
  useEffect(() => {
    const fetchTeamsForBrand = async () => {
      if (userId && firstBrandId !== null) {
        // Skip if we already fetched teams for this brand
        if (lastFetchedBrandIdRef.current === firstBrandId) {
          return;
        }

        try {
          const teams = await dmgAlertApiService.getTeams(userId, firstBrandId);
          setTeamUsers(teams);
          lastFetchedBrandIdRef.current = firstBrandId; // Mark as fetched
        } catch (error) {
          console.error('Error fetching teams for selected brand:', error);
        }
      }
    };

    fetchTeamsForBrand();
  }, [firstBrandId, userId]);

  // Track the last processed recipients to prevent loops
  const lastProcessedRecipientsRef = useRef<string>('');

  // Update liftNotificationUserRecipients to match teamUsers format after teamUsers loads
  useEffect(() => {
    if (
      teamUsers.length > 0 &&
      formData.liftNotificationUserRecipients &&
      formData.liftNotificationUserRecipients.length > 0
    ) {
      const currentRecipients = JSON.stringify(formData.liftNotificationUserRecipients);

      // Skip if we already processed these exact recipients
      if (lastProcessedRecipientsRef.current === currentRecipients) {
        return;
      }

      // Update liftNotificationUserRecipients to use exact format from teamUsers
      const updatedRecipients = formData.liftNotificationUserRecipients.map(recipientStr => {
        try {
          const parsed = JSON.parse(recipientStr);
          // Find matching user in teamUsers by ID
          const matchedUser = teamUsers.find(user => user.id === parsed.id);
          if (matchedUser) {
            // Return exact format that matches MultiSelectTag options
            return JSON.stringify({
              id: matchedUser.id,
              name: matchedUser.name,
              email: matchedUser.email,
            });
          }
          // If no match found, return original
          return recipientStr;
        } catch {
          return recipientStr;
        }
      });

      // Only update if there's a change
      const updatedStr = JSON.stringify(updatedRecipients);
      if (currentRecipients !== updatedStr) {
        setFormData(prev => ({
          ...prev,
          liftNotificationUserRecipients: updatedRecipients,
        }));
        lastProcessedRecipientsRef.current = updatedStr;
      } else {
        lastProcessedRecipientsRef.current = currentRecipients;
      }
    }
    // setFormData is stable from useState, no need to include in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamUsers, formData.liftNotificationUserRecipients]);

  const handleAlertTypeChange = (displayName: string) => {
    const previousToolName = formData.toolName;

    // If tool is cleared, clear the formData and brands
    if (!displayName || displayName.trim() === '') {
      setFormData(prev => ({
        ...prev,
        toolName: '',
        alertTypeId: undefined,
        alertMetric: undefined,
      }));
      // Clear all selected brands
      setSelectedBrands([]);
      return;
    }

    const selectedAlertType = alertTypes.find(at => at.display_name === displayName);
    if (selectedAlertType) {
      // If switching to a different tool (not initial load), reset everything from step 2 onwards
      if (previousToolName && previousToolName !== displayName) {
        // Clear all selected brands
        setSelectedBrands([]);

        // Reset communication methods to unselected state
        setFormData(prev => ({
          ...prev,
          communicationMethods: prev.communicationMethods.map(method => ({
            ...method,
            selected: false,
          })),
          // Clear all recipients
          slackRecipients: [],
          emailRecipients: [],
          emailCcRecipients: [],
          liftRecipients: [],
          liftNotificationUserRecipients: [],
          // Clear Alert Prompt Message
          alertMessage: '',
          // Clear message templates
          messageTemplates: {
            slack: '',
            email: '',
            web: '',
          },
          // Reset schedule config to defaults
          cadence: 'realtime',
          scheduleConfig: {
            time: '09:00',
            timezone: 'America/New_York',
            days: [1, 2, 3, 4, 5],
            day: 1,
          },
        }));

        // Reset state variables for communication methods and recipients
        setSelectedCommunicationMethods([]);
        setSelectedSlackRecipients([]);
      }

      const def = selectedAlertType.default_threshold_config?.[0];
      setFormData(prev => ({
        ...prev,
        toolName: displayName,
        alertTypeId: selectedAlertType.id,
        alertMetric: def?.metric || prev.alertMetric,
      }));
      if (def && selectedBrands.length > 0) {
        // Prefill operator/threshold for existing rows
        setSelectedBrands(prev =>
          prev.map(b => ({
            ...b,
            thresholds: b.thresholds.map(t => ({
              ...t,
              operator: def.operator ?? t.operator,
              threshold: def.value ?? t.threshold,
            })),
          }))
        );
      }
    }
  };

  const handleCommunicationMethodChange = (selectedIds: string[]) => {
    // Find the IDs for "LIFT notifications for a Brand" and "LIFT notifications for a user"
    const liftBrandMethod = formData.communicationMethods.find(
      m => m.type === 'LIFT notifications for a Brand'
    );
    const liftUserMethod = formData.communicationMethods.find(
      m => m.type === 'LIFT notifications for a user'
    );

    // Check if either LIFT notification type is being selected
    const isSelectingLiftBrand = liftBrandMethod && selectedIds.includes(liftBrandMethod.id);
    const isSelectingLiftUser = liftUserMethod && selectedIds.includes(liftUserMethod.id);

    // Make them mutually exclusive
    if (isSelectingLiftBrand && isSelectingLiftUser) {
      // Check which one was previously selected
      const wasLiftBrandSelected = selectedCommunicationMethods.includes(liftBrandMethod?.id || '');
      const wasLiftUserSelected = selectedCommunicationMethods.includes(liftUserMethod?.id || '');

      // Determine which one was just added (the one that wasn't selected before)
      const justAddedLiftBrand = isSelectingLiftBrand && !wasLiftBrandSelected;
      const justAddedLiftUser = isSelectingLiftUser && !wasLiftUserSelected;

      if (justAddedLiftBrand) {
        // Brand was just added, remove user
        selectedIds = selectedIds.filter(id => id !== liftUserMethod?.id);
      } else if (justAddedLiftUser) {
        // User was just added, remove brand
        selectedIds = selectedIds.filter(id => id !== liftBrandMethod?.id);
      } else {
        // Both were already selected, this shouldn't happen, but remove user as default
        selectedIds = selectedIds.filter(id => id !== liftUserMethod?.id);
      }
    } else if (isSelectingLiftBrand) {
      // Only brand is selected, ensure user is not selected
      selectedIds = selectedIds.filter(id => id !== liftUserMethod?.id);
    } else if (isSelectingLiftUser) {
      // Only user is selected, ensure brand is not selected
      selectedIds = selectedIds.filter(id => id !== liftBrandMethod?.id);
    }

    const newlyAddedIds = selectedIds.filter(id => !selectedCommunicationMethods.includes(id));

    let nextSlack = formData.slackRecipients ?? [];
    let nextEmail = formData.emailRecipients ?? [];
    let nextEmailCc = formData.emailCcRecipients ?? [];
    let nextLiftUser = formData.liftNotificationUserRecipients ?? [];

    for (const id of newlyAddedIds) {
      const method = formData.communicationMethods.find(m => m.id === id);
      if (!method || !userEmail) continue;

      if (method.type === 'Slack' && slackUsers.length > 0) {
        const currentUserInSlack = slackUsers.find(
          u => u.email?.toLowerCase() === userEmail.toLowerCase()
        );
        if (currentUserInSlack) {
          const teamUser = teamUsers.find(
            tu => tu.email?.toLowerCase() === currentUserInSlack.email?.toLowerCase()
          );
          const slackValue = JSON.stringify({
            type: 'user',
            id: currentUserInSlack.slack_id,
            userId: teamUser?.id ?? currentUserInSlack.id ?? null,
            email: currentUserInSlack.email,
            name: currentUserInSlack.real_name || currentUserInSlack.name,
          });
          const alreadyInSlack = nextSlack.some((val: string) => {
            try {
              const parsed = JSON.parse(val);
              return parsed.type === 'user' && parsed.id === currentUserInSlack.slack_id;
            } catch {
              return false;
            }
          });
          if (!alreadyInSlack) {
            nextSlack = [...nextSlack, slackValue];
          }
        }
      } else if (method.type === 'Email' && teamUsers.length > 0) {
        const currentUserInTeam = teamUsers.find(
          u => u.email?.toLowerCase() === userEmail.toLowerCase()
        );
        if (currentUserInTeam) {
          const userValue = JSON.stringify({
            id: currentUserInTeam.id,
            name: currentUserInTeam.name,
            email: currentUserInTeam.email,
          });
          const alreadyInEmail = nextEmail.some((val: string) => {
            try {
              const parsed = JSON.parse(val);
              return parsed.email?.toLowerCase() === userEmail.toLowerCase();
            } catch {
              return (val as string).toLowerCase() === userEmail.toLowerCase();
            }
          });
          if (!alreadyInEmail) {
            nextEmail = [...nextEmail, userValue];
          }
        }
      } else if (method.type === 'LIFT notifications for a user' && teamUsers.length > 0) {
        const currentUserInTeam = teamUsers.find(
          u => u.email?.toLowerCase() === userEmail.toLowerCase()
        );
        if (currentUserInTeam) {
          const userValue = JSON.stringify({
            id: currentUserInTeam.id,
            name: currentUserInTeam.name,
            email: currentUserInTeam.email,
          });
          const alreadyInLift = nextLiftUser.some((val: string) => {
            try {
              const parsed = JSON.parse(val);
              return parsed.email?.toLowerCase() === userEmail.toLowerCase();
            } catch {
              return (val as string).toLowerCase() === userEmail.toLowerCase();
            }
          });
          if (!alreadyInLift) {
            nextLiftUser = [...nextLiftUser, userValue];
          }
        }
      }
    }

    setSelectedCommunicationMethods(selectedIds);
    setFormData(prev => ({
      ...prev,
      communicationMethods: prev.communicationMethods.map(m => ({
        ...m,
        selected: selectedIds.includes(m.id),
      })),
      slackRecipients: nextSlack,
      emailRecipients: nextEmail,
      emailCcRecipients: nextEmailCc,
      liftNotificationUserRecipients: nextLiftUser,
    }));
    setSelectedSlackRecipients(nextSlack);
  };

  const handleSlackRecipientChange = (selectedIds: string[]) => {
    setSelectedSlackRecipients(selectedIds);
    setFormData(prev => ({
      ...prev,
      slackRecipients: selectedIds,
    }));
  };

  const handleEmailRecipientsChange = (emails: string[]) => {
    setFormData(prev => ({
      ...prev,
      emailRecipients: emails,
    }));
  };

  const handleEmailCcRecipientsChange = (emails: string[]) => {
    setFormData(prev => ({
      ...prev,
      emailCcRecipients: emails,
    }));
  };

  const handleLiftRecipientsChange = (recipients: string[]) => {
    setFormData(prev => ({
      ...prev,
      liftNotificationUserRecipients: recipients,
    }));
  };

  const handleMessageTemplateChange = (channel: 'slack' | 'email' | 'web', content: string) => {
    setFormData(prev => ({
      ...prev,
      messageTemplates: {
        ...prev.messageTemplates,
        slack: prev.messageTemplates?.slack || '',
        email: prev.messageTemplates?.email || '',
        web: prev.messageTemplates?.web || '',
        [channel]: content,
      },
    }));
  };

  const handleScheduleConfigChange = (
    field: 'cadence' | 'time' | 'timezone' | 'days' | 'day',
    value: any
  ) => {
    if (field === 'cadence') {
      setFormData(prev => ({ ...prev, cadence: value }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      scheduleConfig: {
        ...(prev.scheduleConfig || {}),
        [field]: value,
      },
    }));
  };

  // Calculate completed steps based on validation
  const getCompletedSteps = React.useCallback((): number[] => {
    const completed: number[] = [];

    // Step 1 is completed if basic info is filled
    if (formData.alertName.trim().length > 0 && formData.description.trim().length > 0) {
      completed.push(0); // Step 1 is index 0
    }

    // Step 2 is completed if alert details are configured
    if (formData.toolName && formData.toolName.trim().length > 0) {
      const selectedAlertType = alertTypes.find(at => at.display_name === formData.toolName);
      const hasBrandIdInConfig =
        selectedAlertType?.alert_query_config?.properties?.includes('brand_id') || false;
      const isPacingTool = selectedAlertType?.name === 'pacing';
      const isTaxonomyTool = selectedAlertType?.name === 'taxonomy';
      const shouldShowBrands = hasBrandIdInConfig || isPacingTool || isTaxonomyTool;

      if (!shouldShowBrands || selectedBrands.length > 0) {
        if (
          !shouldShowBrands ||
          selectedBrands.every(brandConfig => {
            // For taxonomy tool, check if there's at least one valid threshold
            if (isTaxonomyTool) {
              const hasAtLeastOneValidThreshold = brandConfig.thresholds.some(threshold => {
                const hasTaxonomyType =
                  threshold.taxonomyType &&
                  (threshold.taxonomyType === 'ad' ||
                    threshold.taxonomyType === 'adset' ||
                    threshold.taxonomyType === 'campaign');
                const hasTaxonomyValueType =
                  threshold.taxonomyValueType &&
                  (threshold.taxonomyValueType === 'percentage' ||
                    threshold.taxonomyValueType === 'count' ||
                    threshold.taxonomyValueType === 'updated_at');
                let hasValidThreshold = false;
                if (threshold.taxonomyValueType === 'updated_at') {
                  if (threshold.threshold && typeof threshold.threshold === 'string') {
                    const date = new Date(threshold.threshold);
                    hasValidThreshold = !isNaN(date.getTime()) && date.getFullYear() > 1970;
                  }
                } else {
                  const thresholdValue =
                    threshold.threshold !== null && threshold.threshold !== undefined
                      ? threshold.threshold
                      : '';
                  const thresholdError = validateThreshold(thresholdValue);
                  hasValidThreshold = thresholdError === null && Number(thresholdValue) >= 0;
                }
                return hasTaxonomyType && hasTaxonomyValueType && hasValidThreshold;
              });
              return hasAtLeastOneValidThreshold;
            }
            // For other tools, check all thresholds
            return brandConfig.thresholds.every(threshold => {
              // For pacing tool, check Dimension, Metric, and Date Range
              if (isPacingTool) {
                const hasDimension =
                  typeof threshold.dimension === 'string'
                    ? threshold.dimension.trim().length > 0
                    : Boolean(threshold.dimension);
                const hasMetric =
                  typeof threshold.metric === 'string'
                    ? threshold.metric.trim().length > 0
                    : Boolean(threshold.metric);
                const isRelative = threshold.calendarType === 'relative';
                const hasDateRange = isRelative
                  ? threshold.comparisonDate &&
                    typeof threshold.comparisonDate === 'string' &&
                    threshold.comparisonDate.trim().length > 0
                  : threshold.startDate &&
                    typeof threshold.startDate === 'string' &&
                    threshold.startDate.trim().length > 0 &&
                    threshold.endDate &&
                    typeof threshold.endDate === 'string' &&
                    threshold.endDate.trim().length > 0;
                const thresholdValue =
                  threshold.threshold !== null && threshold.threshold !== undefined
                    ? Number(threshold.threshold)
                    : 0;
                const hasValidThreshold = !isNaN(thresholdValue) && thresholdValue >= 0;
                return hasDimension && hasMetric && hasDateRange && hasValidThreshold;
              }
              // For other tools (like EMQ), check Pixel and Event
              const hasPixel = threshold.pixelId && threshold.pixelId.trim().length > 0;
              const hasEvent =
                threshold.eventId &&
                threshold.eventId > 0 &&
                threshold.eventName &&
                threshold.eventName.trim().length > 0;
              const thresholdValue =
                threshold.threshold !== null && threshold.threshold !== undefined
                  ? Number(threshold.threshold)
                  : 0;
              const hasValidThreshold = !isNaN(thresholdValue) && thresholdValue >= 0;
              return hasPixel && hasEvent && hasValidThreshold;
            });
          })
        ) {
          completed.push(1); // Step 2 is index 1
        }
      }
    }

    // Step 3 is completed if communication methods are selected
    if (selectedCommunicationMethods.length > 0) {
      const slackValidation = selectedCommunicationMethods.some(
        id => formData.communicationMethods.find(m => m.id === id)?.type === 'Slack'
      )
        ? selectedSlackRecipients.length > 0
        : true;
      if (slackValidation) {
        completed.push(2); // Step 3 is index 2
      }
    }

    // Step 4 is completed if message templates are filled based on selected communication methods
    const hasSlackSelected = selectedCommunicationMethods.some(
      id => formData.communicationMethods.find(m => m.id === id)?.type === 'Slack'
    );
    const hasEmailSelected = selectedCommunicationMethods.some(
      id => formData.communicationMethods.find(m => m.id === id)?.type === 'Email'
    );
    const hasWebSelected = selectedCommunicationMethods.some(id => {
      const method = formData.communicationMethods.find(m => m.id === id);
      return (
        method?.type === 'LIFT notifications for a Brand' ||
        method?.type === 'LIFT notifications for a user'
      );
    });

    const slackTemplateFilled =
      stripHtmlToPlainText(formData.messageTemplates?.slack).trim().length > 0;
    const emailTemplateFilled =
      stripHtmlToPlainText(formData.messageTemplates?.email).trim().length > 0;
    const webTemplateFilled =
      stripHtmlToPlainText(formData.messageTemplates?.web).trim().length > 0;

    const slackValid = !hasSlackSelected || slackTemplateFilled;
    const emailValid = !hasEmailSelected || emailTemplateFilled;
    const webValid = !hasWebSelected || webTemplateFilled;

    if (
      slackValid &&
      emailValid &&
      webValid &&
      (hasSlackSelected || hasEmailSelected || hasWebSelected)
    ) {
      completed.push(3); // Step 4 is index 3
    }

    return completed;
  }, [formData, alertTypes, selectedBrands, selectedCommunicationMethods, selectedSlackRecipients]);

  // Notify parent of step changes - only when step actually changes
  const prevStepRef = React.useRef(currentStep);
  const prevCompletedStepsRef = React.useRef<number[]>([]);

  useEffect(() => {
    if (onStepChange) {
      const completedSteps = getCompletedSteps();
      const stepChanged = prevStepRef.current !== currentStep;

      // Compare arrays by value, not reference
      const arraysEqual = (a: number[], b: number[]) => {
        if (a.length !== b.length) return false;
        return a.every((val, idx) => val === b[idx]);
      };

      const completedStepsChanged = !arraysEqual(prevCompletedStepsRef.current, completedSteps);

      // Only call onStepChange if step or completedSteps actually changed
      if (stepChanged || completedStepsChanged) {
        onStepChange(currentStep, completedSteps);
        prevStepRef.current = currentStep;
        prevCompletedStepsRef.current = [...completedSteps]; // Store a copy
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, getCompletedSteps]);

  const handleStepClick = async (step: number) => {
    if (step >= 1 && step <= 5) {
      if (currentStep === 4 && step > 4) {
        if (!isStepValid()) {
          Toastify('error', 'Please fill in message templates for all selected channels');
          return;
        }
        if (messageTemplateStepRef.current) {
          const canProceed = await messageTemplateStepRef.current.shouldBlockNavigation();
          if (!canProceed) return;
        }
      }
      setCurrentStep(step);
    }
  };

  // Expose handleStepClick via ref
  React.useImperativeHandle(ref, () => ({
    handleStepClick,
  }));

  const handleNext = async () => {
    // Check for pacing threshold validation on step 2 (Alert Details)
    if (currentStep === 2) {
      const selectedAlertType = alertTypes.find(at => at.display_name === formData.toolName);
      const isPacingTool = selectedAlertType?.name === 'pacing';

      if (isPacingTool) {
        // For percentage value type only: threshold cannot be more than 100
        const hasInvalidThreshold = selectedBrands.some(brandConfig =>
          brandConfig.thresholds.some(threshold => {
            const isPercentage =
              !threshold.pacingValueType || threshold.pacingValueType === 'percentage';
            if (!isPercentage) return false; // No cap for "number" (count)
            const thresholdValue =
              typeof threshold.threshold === 'string'
                ? parseFloat(threshold.threshold)
                : threshold.threshold;
            return (
              thresholdValue !== null &&
              thresholdValue !== undefined &&
              !isNaN(thresholdValue) &&
              thresholdValue > 100
            );
          })
        );

        if (hasInvalidThreshold) {
          Toastify('error', 'Percentage cannot be more than 100');
          return;
        }
      }
    }

    // Check if navigation should be blocked on step 4 (Message Template)
    if (currentStep === 4 && messageTemplateStepRef.current) {
      const canProceed = await messageTemplateStepRef.current.shouldBlockNavigation();
      if (!canProceed) {
        return; // User cancelled, don't proceed
      }
    }

    if (currentStep < 5) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handlePreview = async () => {
    if (messageTemplateStepRef.current) {
      await messageTemplateStepRef.current.triggerPreview();
      // Update local state after preview is triggered
      const newState = messageTemplateStepRef.current.getPreviewState();
      setPreviewState(newState);
    }
  };

  // Sync preview state from MessageTemplateStep
  useEffect(() => {
    if (currentStep === 4 && messageTemplateStepRef.current) {
      const interval = window.setInterval(() => {
        const state = messageTemplateStepRef.current?.getPreviewState();
        if (state) {
          setPreviewState(prev => {
            if (
              prev.showPreview !== state.showPreview ||
              prev.isLoadingPreview !== state.isLoadingPreview
            ) {
              return state;
            }
            return prev;
          });
        }
      }, 100);
      return () => window.clearInterval(interval);
    }
  }, [currentStep]);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    // Check if navigation should be blocked on step 4 (Message Template)
    if (currentStep === 4 && messageTemplateStepRef.current) {
      const canProceed = await messageTemplateStepRef.current.shouldBlockNavigation();
      if (!canProceed) {
        return; // User cancelled, don't proceed
      }
    }

    setIsSubmitting(true);
    try {
      // Use ref to get the absolute latest selectedBrands value
      await onSubmit(formData, selectedBrandsRef.current);
    } catch (error) {
      console.error('Error submitting alert:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.alertName.trim().length > 0 && formData.description.trim().length > 0;
      case 2: {
        const baseValidation = formData.toolName && formData.toolName.trim().length > 0;
        if (!baseValidation) return false;

        const selectedAlertType = alertTypes.find(at => at.display_name === formData.toolName);
        const hasBrandIdInConfig =
          selectedAlertType?.alert_query_config?.properties?.includes('brand_id') || false;
        const isPacingTool = selectedAlertType?.name === 'pacing';
        const isTaxonomyTool = selectedAlertType?.name === 'taxonomy';
        const shouldShowBrands = hasBrandIdInConfig || isPacingTool || isTaxonomyTool;

        // If brands are not required, just validate tool is selected
        if (!shouldShowBrands) {
          return true;
        }

        // Validate brands are selected
        const brandValidation = selectedBrands.length > 0;
        if (!brandValidation) return false;

        // Get max_value from alert type for validation
        const maxValue =
          selectedAlertType?.default_threshold_config?.[0]?.max_value !== undefined
            ? parseFloat(String(selectedAlertType.default_threshold_config[0].max_value))
            : undefined;

        // Validate all thresholds have required fields filled and valid values
        const thresholdValidation = selectedBrands.every(brandConfig => {
          // Check that there's at least one threshold row
          if (brandConfig.thresholds.length === 0) {
            return false;
          }

          // For taxonomy tool, check if there's at least one valid threshold
          if (isTaxonomyTool) {
            const hasAtLeastOneValidThreshold = brandConfig.thresholds.some(threshold => {
              const hasTaxonomyType =
                threshold.taxonomyType &&
                (threshold.taxonomyType === 'ad' ||
                  threshold.taxonomyType === 'adset' ||
                  threshold.taxonomyType === 'campaign');
              const hasTaxonomyValueType =
                threshold.taxonomyValueType &&
                (threshold.taxonomyValueType === 'percentage' ||
                  threshold.taxonomyValueType === 'count' ||
                  threshold.taxonomyValueType === 'updated_at');
              let hasValidThreshold = false;
              if (threshold.taxonomyValueType === 'updated_at') {
                if (threshold.threshold && typeof threshold.threshold === 'string') {
                  const date = new Date(threshold.threshold);
                  hasValidThreshold = !isNaN(date.getTime()) && date.getFullYear() > 1970;
                }
              } else {
                // Get the correct max value based on value type
                // Skip max value validation for 'count' type
                const taxonomyMaxValue =
                  threshold.taxonomyValueType === 'count'
                    ? undefined
                    : (() => {
                        const matchingConfig = selectedAlertType?.default_threshold_config?.find(
                          config =>
                            'metric' in config && config.metric === threshold.taxonomyValueType
                        );
                        if (
                          matchingConfig &&
                          'max_value' in matchingConfig &&
                          matchingConfig.max_value !== undefined
                        ) {
                          return parseFloat(String(matchingConfig.max_value));
                        }
                        return maxValue;
                      })();
                const thresholdError = validateThreshold(threshold.threshold, taxonomyMaxValue);
                hasValidThreshold = thresholdError === null;
              }
              return hasTaxonomyType && hasTaxonomyValueType && hasValidThreshold;
            });

            if (!hasAtLeastOneValidThreshold) {
              return false;
            }
          }

          return brandConfig.thresholds.every(threshold => {
            // For pacing tool, check Dimension, Metric, and Threshold value
            if (isPacingTool) {
              // Check if dimension is selected
              // Dimension is stored as comma-separated string in threshold.dimension
              // Or as JSON string in dimensionValue
              let hasDimension = false;

              if (threshold.dimension) {
                // Dimension is stored as comma-separated string
                const dimensionStr =
                  typeof threshold.dimension === 'string'
                    ? threshold.dimension.trim()
                    : String(threshold.dimension);
                hasDimension = dimensionStr.length > 0;
              } else if (threshold.dimensionValue) {
                // Fallback: check dimensionValue (JSON string)
                try {
                  const parsed =
                    typeof threshold.dimensionValue === 'string'
                      ? JSON.parse(threshold.dimensionValue)
                      : threshold.dimensionValue;
                  hasDimension = Array.isArray(parsed) ? parsed.length > 0 : Boolean(parsed);
                } catch {
                  hasDimension = Boolean(threshold.dimensionValue);
                }
              }

              // Check if metric is selected
              const hasMetric = threshold.metric && threshold.metric.trim().length > 0;

              // Check date range: relative needs comparisonDate, calendar/fiscal need start+end
              const isRelative = threshold.calendarType === 'relative';
              const hasDateRange = isRelative
                ? threshold.comparisonDate &&
                  typeof threshold.comparisonDate === 'string' &&
                  threshold.comparisonDate.trim().length > 0
                : threshold.startDate &&
                  typeof threshold.startDate === 'string' &&
                  threshold.startDate.trim().length > 0 &&
                  threshold.endDate &&
                  typeof threshold.endDate === 'string' &&
                  threshold.endDate.trim().length > 0;

              // Validate threshold value using the same validation function
              // For pacing, allow 0 as a valid threshold (0% is valid)
              const thresholdValue =
                threshold.threshold !== null && threshold.threshold !== undefined
                  ? threshold.threshold
                  : '';
              const thresholdError = validateThreshold(thresholdValue, maxValue);
              const hasValidThreshold = thresholdError === null;

              const isValid = hasDimension && hasMetric && hasDateRange && hasValidThreshold;

              // All fields must be filled and valid
              return isValid;
            }
            // For taxonomy tool, check Taxonomy Type, Value Type, and Threshold
            if (isTaxonomyTool) {
              // Check if row has any field filled (to determine if it should be validated)
              const hasAnyField =
                (threshold.taxonomyType &&
                  (threshold.taxonomyType === 'ad' ||
                    threshold.taxonomyType === 'adset' ||
                    threshold.taxonomyType === 'campaign')) ||
                (threshold.taxonomyValueType &&
                  (threshold.taxonomyValueType === 'percentage' ||
                    threshold.taxonomyValueType === 'count' ||
                    threshold.taxonomyValueType === 'updated_at')) ||
                (threshold.threshold !== null && threshold.threshold !== undefined);

              // If row is completely empty, skip validation (allow empty rows)
              if (!hasAnyField) {
                return true;
              }

              // Check if taxonomy type is selected
              const hasTaxonomyType =
                threshold.taxonomyType &&
                (threshold.taxonomyType === 'ad' ||
                  threshold.taxonomyType === 'adset' ||
                  threshold.taxonomyType === 'campaign');
              // Check if taxonomy value type is selected
              const hasTaxonomyValueType =
                threshold.taxonomyValueType &&
                (threshold.taxonomyValueType === 'percentage' ||
                  threshold.taxonomyValueType === 'count' ||
                  threshold.taxonomyValueType === 'updated_at');
              // Validate threshold value based on value type
              let hasValidThreshold = false;
              if (threshold.taxonomyValueType === 'updated_at') {
                // For updated_at, check if it's a valid date string
                if (threshold.threshold && typeof threshold.threshold === 'string') {
                  const date = new Date(threshold.threshold);
                  hasValidThreshold = !isNaN(date.getTime()) && date.getFullYear() > 1970;
                }
              } else {
                // For percentage and count, get the correct max value based on value type
                // Skip max value validation for 'count' type
                const taxonomyMaxValue =
                  threshold.taxonomyValueType === 'count'
                    ? undefined
                    : (() => {
                        const matchingConfig = selectedAlertType?.default_threshold_config?.find(
                          config =>
                            'metric' in config && config.metric === threshold.taxonomyValueType
                        );
                        if (
                          matchingConfig &&
                          'max_value' in matchingConfig &&
                          matchingConfig.max_value !== undefined
                        ) {
                          return parseFloat(String(matchingConfig.max_value));
                        }
                        return maxValue; // Fallback to first config
                      })();
                const thresholdError = validateThreshold(threshold.threshold, taxonomyMaxValue);
                hasValidThreshold = thresholdError === null;
              }
              // All fields must be filled and valid
              return hasTaxonomyType && hasTaxonomyValueType && hasValidThreshold;
            }
            // For other tools (like EMQ), check Pixel and Event
            // Check if pixel is selected
            const hasPixel = threshold.pixelId && threshold.pixelId.trim().length > 0;
            // Check if event is selected - eventName is required, eventId is optional (might be missing in some cases)
            const hasEvent = threshold.eventName && threshold.eventName.trim().length > 0;
            // Validate threshold value using the same validation function
            const thresholdError = validateThreshold(threshold.threshold, maxValue);
            const hasValidThreshold = thresholdError === null;
            // All fields must be filled and valid
            return hasPixel && hasEvent && hasValidThreshold;
          });
        });

        return thresholdValidation;
      }
      case 3: {
        const hasAtLeastOneMethod = selectedCommunicationMethods.length > 0;
        if (!hasAtLeastOneMethod) return false;

        const hasSlackSelected = selectedCommunicationMethods.some(
          id => formData.communicationMethods.find(m => m.id === id)?.type === 'Slack'
        );
        const hasEmailSelected = selectedCommunicationMethods.some(
          id => formData.communicationMethods.find(m => m.id === id)?.type === 'Email'
        );
        const hasLiftUserSelected = selectedCommunicationMethods.some(
          id =>
            formData.communicationMethods.find(m => m.id === id)?.type ===
            'LIFT notifications for a user'
        );

        const slackValid = !hasSlackSelected || selectedSlackRecipients.length > 0;
        const emailValid = !hasEmailSelected || (formData.emailRecipients?.length ?? 0) > 0;
        const liftUserValid =
          !hasLiftUserSelected || (formData.liftNotificationUserRecipients?.length ?? 0) > 0;

        return slackValid && emailValid && liftUserValid;
      }
      case 4: {
        // Check which communication methods are selected
        const hasSlackSelected = selectedCommunicationMethods.some(
          id => formData.communicationMethods.find(m => m.id === id)?.type === 'Slack'
        );
        const hasEmailSelected = selectedCommunicationMethods.some(
          id => formData.communicationMethods.find(m => m.id === id)?.type === 'Email'
        );
        const hasWebSelected = selectedCommunicationMethods.some(id => {
          const method = formData.communicationMethods.find(m => m.id === id);
          return (
            method?.type === 'LIFT notifications for a Brand' ||
            method?.type === 'LIFT notifications for a user'
          );
        });

        // Check if corresponding templates are filled (strip HTML to detect empty Quill content like <p><br></p>)
        const slackTemplateFilled =
          stripHtmlToPlainText(formData.messageTemplates?.slack).trim().length > 0;
        const emailTemplateFilled =
          stripHtmlToPlainText(formData.messageTemplates?.email).trim().length > 0;
        const webTemplateFilled =
          stripHtmlToPlainText(formData.messageTemplates?.web).trim().length > 0;

        // Validate: if a method is selected, its template must be filled
        const slackValid = !hasSlackSelected || slackTemplateFilled;
        const emailValid = !hasEmailSelected || emailTemplateFilled;
        const webValid = !hasWebSelected || webTemplateFilled;

        // At least one communication method must be selected
        const hasAtLeastOneMethod = hasSlackSelected || hasEmailSelected || hasWebSelected;

        return slackValid && emailValid && webValid && hasAtLeastOneMethod;
      }
      case 5:
        return true; // Summary step is always valid (read-only review)
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicInformationStep
            formData={formData}
            onInputChange={handleInputChange}
            isEditMode={!!alert && !isDuplicating}
          />
        );

      case 2:
        return (
          <AlertDetailsStep
            formData={formData}
            alertTypes={alertTypes}
            brands={brands}
            selectedBrands={selectedBrands}
            pixels={pixels}
            getEventsForThreshold={getEventsForThreshold}
            onAlertTypeChange={handleAlertTypeChange}
            onAddBrand={addBrand}
            onRemoveBrand={removeBrand}
            onPixelSelection={handlePixelSelection}
            onUpdateThreshold={updateThreshold}
            onAddThresholdRow={addThresholdRow}
            onRemoveThresholdRow={removeThresholdRow}
            onReorderThresholds={reorderThresholds}
            onClearAllThresholds={clearAllThresholds}
            isLoadingEvents={isLoadingEvents}
            isLoadingInitialData={isLoadingInitialData}
            fetchEventsForPixels={fetchEventsForPixels}
            onUpdateLogicalOperator={updateLogicalOperator}
            isEditMode={!!alert && !isDuplicating}
          />
        );

      case 3:
        return (
          <CommunicationStep
            formData={formData}
            selectedCommunicationMethods={selectedCommunicationMethods}
            selectedSlackRecipients={selectedSlackRecipients}
            slackUsers={slackUsers}
            slackChannelsWithDetails={slackChannelsWithDetails}
            teamUsers={teamUsers}
            onCommunicationMethodChange={handleCommunicationMethodChange}
            onSlackRecipientChange={handleSlackRecipientChange}
            onEmailRecipientsChange={handleEmailRecipientsChange}
            onEmailCcRecipientsChange={handleEmailCcRecipientsChange}
            onLiftRecipientsChange={handleLiftRecipientsChange}
            userEmail={userEmail}
            userId={userId}
            isEditMode={!!alert && !isDuplicating}
            isLoadingCommunicationData={isLoadingCommunicationData}
          />
        );

      case 4:
        return (
          <MessageTemplateStep
            ref={messageTemplateStepRef}
            formData={formData}
            onInputChange={handleInputChange}
            onMessageTemplateChange={handleMessageTemplateChange}
            userId={userId}
            selectedBrands={selectedBrands}
            alertTypes={alertTypes}
            selectedCommunicationMethods={selectedCommunicationMethods}
            slackUsers={slackUsers}
            slackChannelsWithDetails={slackChannelsWithDetails}
            userEmail={userEmail}
          />
        );

      case 5:
        return (
          <AlertSummaryStep
            formData={formData}
            selectedBrands={selectedBrands}
            onScheduleChange={handleScheduleConfigChange}
            onMessageTemplateChange={handleMessageTemplateChange}
            onRemoveThresholdRow={removeThresholdRow}
            selectedCommunicationMethods={selectedCommunicationMethods}
            userId={userId}
            alertTypes={alertTypes}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Content */}
        <div>{renderStepContent()}</div>
      </div>

      {/* Fixed Footer */}
      <div className="flex justify-between items-center pt-6 border-t border-gray-200">
        {/* Hide Previous button when preview is shown on step 4 */}
        {!(currentStep === 4 && previewState.showPreview) ? (
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
        ) : (
          <div /> /* Placeholder to maintain layout */
        )}

        <div className="flex gap-4">
          {currentStep < 5 ? (
            <>
              {currentStep === 4 && (
                <Button
                  variant={previewState.showPreview ? 'default' : 'outline'}
                  onClick={handlePreview}
                  disabled={previewState.isLoadingPreview}
                  className="flex items-center gap-2"
                >
                  {previewState.isLoadingPreview ? (
                    <>
                      <SpinnerLoader size={14} />
                      <span>Loading...</span>
                    </>
                  ) : previewState.showPreview ? (
                    <>
                      <EyeOff className="h-4 w-4" />
                      Hide Preview
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4" />
                      Preview
                    </>
                  )}
                </Button>
              )}
              {/* Hide Next button when preview is shown on step 4 */}
              {!(currentStep === 4 && previewState.showPreview) && (
                <Button
                  variant="primary"
                  onClick={handleNext}
                  disabled={!isStepValid()}
                  className="flex items-center gap-2"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </>
          ) : (
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmit}
              disabled={!isStepValid() || isSubmitting}
              className="flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <SpinnerLoader size={16} />
                  {alert && !isDuplicating ? 'Updating...' : isDuplicating ? 'Duplicating...' : 'Creating...'}
                </>
              ) : alert && !isDuplicating ? (
                'Update Alert'
              ) : isDuplicating ? (
                'Duplicate Alert'
              ) : (
                'Create Alert'
              )}
            </Button>
          )}
        </div>
      </div>
    </>
  );
});

AlertForm.displayName = 'AlertForm';
